<script setup lang="ts">
import type { BaseItem } from '~/models/Content/BaseItem'
import type { Activity } from '~/models/Activity'
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { KsIcon, KsProgress } from '@aschehoug/kloss'
import { storeToRefs } from 'pinia'
import { useActivityStore } from '~//stores/activity'
import { useAuthStore } from '~/stores/auth'
import { useContentHelper } from '~/composables/useContentHelper'
import { useUrlBuilder } from '~/composables/useUrlBuilder'
import StudentActivityDialog from '~/components/activity/StudentActivityDialog.vue'

const activityStore = useActivityStore()
const { getActivityByLocationId, clearActivityByLocationId } = activityStore
const { isTeacher, hasOrganization, isStudent } = storeToRefs(useAuthStore())
const { getTitle } = useContentHelper()
const { buildResourceUrl } = useUrlBuilder()

const { t } = useI18n()

const props = defineProps<{
  resource: BaseItem
}>()

const completed = ref<boolean>(false)
const inProgress = ref<boolean>(false)

const title = computed(() => getTitle(props.resource))

const activity = computed((): Activity | null => {
  if (!props.resource.locationId) return null
  return getActivityByLocationId(props.resource.locationId)
})

const url = computed(() => buildResourceUrl(props.resource))

const setStudentRefs = () => {
  if (activity.value === null) {
    return
  }

  if (typeof activity.value.sectionCount === 'number' && typeof activity.value.progress === 'number') {
    inProgress.value = activity.value.progress < 100
    completed.value = activity.value.progress === 100
  }

  if (typeof activity.value.sectionCount !== 'number') {
    inProgress.value = false
    completed.value = true
  }
}

watch(activity, async () => {
  if (isStudent.value) {
    setStudentRefs()
  }
})

onBeforeUnmount(() => {
  if (!props.resource.locationId) return null
  clearActivityByLocationId(props.resource.locationId)
})

</script>
<template>
  <StudentActivityDialog
    v-if="isTeacher && hasOrganization && activity"
    :location-activity="activity"
    :card-title="title"
    :url="url"
  />

  <!--   Student specific   -->
  <div
    v-if="isStudent && hasOrganization && completed"
    class="flex items-center gap-1 text-base font-medium text-seagreen-40"
  >
    <KsIcon id="check-circle" />
    <p
      class="text-sm"
      v-text="t('activity.status.visited')"
    />
  </div>
  <div
    v-if="isStudent && hasOrganization && inProgress"
    class="flex items-center gap-2 text-sm font-medium"
  >
    <p
      :style="{color: '#000000'}"
      v-text="activity?.progress + '%'"
    />
    <KsProgress
      :max="activity?.sectionCount ?? 0"
      :values="[activity?.read]"
    />
  </div>
</template>
